import { useCallback, useEffect, useState } from 'react'
import { AuthError, AuthService } from '~/services/auth/AuthService'
import {
  ACCOUNT_BANNED_MESSAGE,
  AUTH_BANNED_EVENT,
  type AuthUser,
  type LoginRequest,
  type RegisterRequest,
  type UpdateProfileRequest,
} from '~/services/auth/types'
import { resolveDefinitiveAuthMessage } from '~/services/auth/utils'
import { AuthContext, type AuthContextValue, type GuestUser } from './context'

// ゲストID生成用のユーティリティ関数
function generateGuestId(): string {
  return `guest_${Math.random().toString(36).substring(2, 11)}`
}

interface Props {
  children: React.ReactNode
}

export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [guestUser, setGuestUser] = useState<GuestUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => {
    setError(null)
    setIsLoading(false) // エラークリア時に isLoading も確実に false にする
  }, [])

  const login = useCallback(async (request: LoginRequest) => {
    try {
      setIsLoading(true)
      setError(null)
      const userData = await AuthService.login(request)
      setUser(userData)
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message)
      } else {
        setError('ログインに失敗しました')
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const register = useCallback(async (request: RegisterRequest) => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await AuthService.register(request)

      // メール確認待ちの場合はログイン状態にしない（LoginScreenで遷移を制御）
      if (response.user && !response.emailVerificationRequired) {
        setUser(response.user)
      }

      return response
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message)
      } else {
        setError('ユーザー登録に失敗しました')
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      setIsLoading(true)
      await AuthService.logout()
    } catch {
      // エラーは無視してログアウト処理を続行
    } finally {
      setUser(null)
      setError(null)
      setIsLoading(false)
    }
  }, [])

  const updateProfile = useCallback(async (request: UpdateProfileRequest) => {
    try {
      setIsLoading(true)
      setError(null)
      const userData = await AuthService.updateProfile(request)
      setUser(userData)
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message)
      } else {
        setError('プロフィールの更新に失敗しました')
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const loginWithVRoidCallback = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const userData = await AuthService.loginWithVRoidCallback()
      setUser(userData)
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message)
      } else {
        setError('VRoidログインに失敗しました')
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const userData = await AuthService.getCurrentUser()
      setUser(userData)
    } catch (err) {
      console.error('ユーザー情報の再取得に失敗しました:', err)
      // 確定的な失効なら getCurrentUser がキャッシュを消しているので React 側も揃える。
      // 放置すると isAuthenticated=true のまま認証付き API だけ失敗し続ける
      const message = resolveDefinitiveAuthMessage(err)
      if (!message) return
      setUser(null)
      setError(message)
    }
  }, [])

  const startGuestSession = useCallback((name: string) => {
    const guestData: GuestUser = {
      id: generateGuestId(),
      displayName: name,
      userIconUrl: null,
      isGuest: true,
    }
    setGuestUser(guestData)
    setError(null)
  }, [])

  const endGuestSession = useCallback(() => {
    setGuestUser(null)
  }, [])

  // BAN検出時にReact状態を強制クリア
  useEffect(() => {
    const handleBanned = () => {
      setUser(null)
      setError(ACCOUNT_BANNED_MESSAGE)
    }
    window.addEventListener(AUTH_BANNED_EVENT, handleBanned)
    return () => window.removeEventListener(AUTH_BANNED_EVENT, handleBanned)
  }, [])

  useEffect(() => {
    const initializeAuth = async () => {
      // 既存 localStorage ユーザーの Cookie 移行を試行
      await AuthService.migrateSession()

      // サーバーにログイン状態を確認する。キャッシュユーザーが無くても 1 回だけ試す:
      // localStorage だけ消えて Cookie は生きている端末（Safari のストレージ削除など）を復帰させるため。
      // ゲストには 401 が 1 本返るだけで、refreshAccessToken はキャッシュ無しだと
      // サーバーに出ないので /auth/refresh は飛ばない。
      const cachedUser = AuthService.getCachedUser()
      try {
        const userData = await AuthService.getCurrentUser()
        setUser(userData)
      } catch (err) {
        setUser(null)
        // キャッシュがあった（＝ログイン中の表示だった）時だけ理由を見せる。
        // getCurrentUser は一過性の失敗ならキャッシュユーザーを返すので、ここに来るのは
        // 確定的な失効（セッション切れ・BAN）だけ。文言は backend の生エラー文を出さず 2 種類に丸める。
        // キャッシュ無しの探りが失敗しただけのゲストには何も見せない
        if (!cachedUser) return
        const message = resolveDefinitiveAuthMessage(err)
        if (message) setError(message)
      }
    }

    initializeAuth().finally(() => setIsLoading(false))
  }, [])

  const value: AuthContextValue = {
    user,
    guestUser,
    isLoading,
    isAuthenticated: Boolean(user),
    isGuest: Boolean(guestUser),
    login,
    register,
    logout,
    updateProfile,
    loginWithVRoidCallback,
    startGuestSession,
    endGuestSession,
    refreshUser,
    error,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export type { AuthContextValue } from './context'
export { useAuth } from './hooks'
